import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"
import { useQuery, UseQueryOptions } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import ResponseError from "#/types/responseError"
import useBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"
import { Journey } from "#/components/carrot-plans/types/journey"

const SPRINTS_CANDIDACY_QUERY_KEY = "sprintsCandidacyQueryKey"

export type SprintsCandidacyResponse = {
  memberIsSprintsCandidate: boolean
  partnerIsSprintsCandidate: boolean
}

export function useSprintsCandidacyQuery<TData = SprintsCandidacyResponse>(
  options?: Pick<UseQueryOptions<SprintsCandidacyResponse, ResponseError, TData>, "select" | "enabled">
) {
  return useQuery<SprintsCandidacyResponse, ResponseError, TData>({
    queryKey: [SPRINTS_CANDIDACY_QUERY_KEY],
    queryFn: () => carrotClient.getSprintsCandidacy(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 0,
    cacheTime: 0,
    retry: buildCarrotApiRetryPolicy(1),
    ...(options || {})
  })
}

export type SprintsAuthorizationResponse = {
  memberIsSprintsAuthorized: boolean
  partnerIsSprintsAuthorized: boolean
}

const SPRINTS_AUTHORIZATION_QUERY_KEY = "sprintsAuthorizationQueryKey"
export function useSprintsAuthorizationQuery<TData = SprintsAuthorizationResponse>(
  options?: Pick<UseQueryOptions<SprintsAuthorizationResponse, ResponseError, TData>, "select" | "enabled">
) {
  return useQuery<SprintsAuthorizationResponse, ResponseError, TData>({
    queryKey: [SPRINTS_AUTHORIZATION_QUERY_KEY],
    queryFn: () => carrotClient.getSprintsAuthorization(),
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    staleTime: 0,
    cacheTime: 0,
    retry: buildCarrotApiRetryPolicy(1),
    ...(options || {})
  })
}

export function useIsSprintsVisible() {
  const { data: isSprintsEnabledByBenefit, isLoading: isBenefitLoading } = useBenefit({
    select: (benefit) => benefit.sprints?.enabled
  })

  const isVisible = isSprintsEnabledByBenefit
  const isLoading = isBenefitLoading

  return { isVisible, isLoading }
}

const SPRINTS_FERTILITY_JOURNEYS = new Set([
  Journey.TRY_PREGNANT,
  Journey.ASSISTED_REPRODUCTION,
  Journey.PRESERVATION,
  Journey.EXPLORING
])

export function useSprintsSidebarCards() {
  const { data: benefit, isLoading: isBenefitLoading } = useBenefit()
  const memberJourneyQuery = useGetMemberJourneyData()

  const resolvedJourney = memberJourneyQuery.data?.currentMemberJourney.preferSubmittedRouting.journey

  const showSprintsForMenopause = !!(benefit?.sprints?.menopause?.enabled && resolvedJourney === Journey.MENOPAUSE)
  const showSprintsForFertility = !!(
    benefit?.sprints?.fertilityCare?.enabled &&
    resolvedJourney != null &&
    SPRINTS_FERTILITY_JOURNEYS.has(resolvedJourney as Journey)
  )

  return {
    showSprintsForMenopause,
    showSprintsForFertility,
    isLoading: isBenefitLoading || memberJourneyQuery.isLoading
  }
}
